import { useUser } from "@clerk/clerk-react";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { clerkEnabled } from "@/services/clerk";
import { describeClerkError as describe } from "@/lib/clerkErrors";
import { useAdminMemberUpdate } from "@/features/admin/admin";
import { useMe } from "@/features/members/hooks/useMe";
import { EmailAddresses } from "@/components/profile/EmailAddresses";
import { FormSkeleton } from "@/components/Skeletons";
import { sectionTitleClass } from "@/lib/typography";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";

/**
 * Sign-in details, the email addresses and the password, as our own page on
 * Clerk's
 * account API, in the profile page's cards. Clerk's `<UserProfile>` was tried
 * first and still looked like Clerk beside everything else, and it carried a
 * second photo control; there is deliberately none here. Name, photo and
 * everything the directory shows live on the profile page.
 */
type User = NonNullable<ReturnType<typeof useUser>["user"]>;

export default function AccountPage() {
  return (
    <div className="max-w-3xl space-y-4">
      <div>
        <h1 className="text-2xl">Settings</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          The addresses you can sign in with, by a code we email or through
          a connected account. Your name, photo and everything the directory
          shows are on{" "}
          <Link to="/profile" className="underline">
            My Profile
          </Link>
          .
        </p>
      </div>
      {clerkEnabled ? (
        <AccountSections />
      ) : (
        <p className="text-muted-foreground text-sm">
          Sign-in is not configured in this environment, so there is no account
          to manage.
        </p>
      )}
    </div>
  );
}

/**
 * Whether this admin hears about the queues.
 *
 * Theirs to set rather than something granted with the role: being given the
 * keys is not the same as asking to be paged. Off by default, and absent for
 * everybody else — nobody without the role can be in the list it feeds, so a
 * switch on their settings page would do nothing.
 */
function AdminNotifications() {
  const { data: me } = useMe(clerkEnabled);
  const update = useAdminMemberUpdate();
  // What was just chosen, shown at once. The saved value arrives with the next
  // `me`, and until 2026-09-17 nothing refetched it: the switch sat unchanged
  // until a reload, so June toggled it off without seeing that she had.
  const [chosen, setChosen] = useState<boolean | null>(null);
  const member = me?.member;
  if (!member || member.role !== "admin") return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className={sectionTitleClass}>Notifications</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <Label htmlFor="admin-notifications">
              Subscribe to Admin Email Notifications
            </Label>
            <p className="text-muted-foreground mt-0.5 text-sm">
              A new registration, a new organization, a message sent from the help
              page, and replies to AFELL's emails.
            </p>
          </div>
          <Switch
            id="admin-notifications"
            checked={chosen ?? Boolean(member.adminNotifications)}
            disabled={update.isPending}
            onCheckedChange={(adminNotifications) => {
              setChosen(adminNotifications);
              update.mutate(
                { id: member.id, adminNotifications },
                { onError: () => setChosen(null) }
              );
            }}
          />
        </div>
        {update.error && (
          <p className="text-destructive mt-2 text-sm">{update.error.message}</p>
        )}
      </CardContent>
    </Card>
  );
}

/** Split out so the Clerk hook only runs under a ClerkProvider. */
function AccountSections() {
  const { user, isLoaded } = useUser();
  if (!isLoaded || !user) return <FormSkeleton />;
  return (
    <>
      <EmailAddresses user={user} />
      <AdminNotifications />
      {user.externalAccounts.length > 0 && <ConnectedAccounts user={user} />}
      {user.deleteSelfEnabled && <DeleteAccount user={user} />}
    </>
  );
}

/** Listed, not managed: disconnecting the only way somebody signs in is how
 *  people lock themselves out, and a password above is the way to add one. */
function ConnectedAccounts({ user }: { user: User }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className={sectionTitleClass}>Connected accounts</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-1 text-sm">
          {user.externalAccounts.map((account) => (
            <li key={account.id} className="flex items-center gap-2">
              {account.provider === "google" ? <GoogleMark /> : null}
              <span className="capitalize">{account.provider}</span>
              <span className="text-muted-foreground">{account.emailAddress}</span>
            </li>
          ))}
        </ul>
        <p className="text-muted-foreground mt-2 text-xs">
          Sign in with these, or with a code emailed to any address above.
        </p>
      </CardContent>
    </Card>
  );
}

/** Google's mark, for a connected Google account. */
function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" className="size-4 shrink-0" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.5 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.8 6.1C12.3 13.6 17.7 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17z" />
      <path fill="#FBBC05" d="M10.4 28.6A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.2.8-4.6l-7.8-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.7l7.8-6.1z" />
      <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.5-5.8c-2.1 1.4-4.8 2.3-8.4 2.3-6.3 0-11.7-4.1-13.6-9.9l-7.8 6.1C6.5 42.6 14.6 48 24 48z" />
    </svg>
  );
}

/**
 * The account, gone: the sign-in is removed at Clerk, and our webhook does the
 * rest — the subscription stops at the end of the paid period, the admins are
 * told, and the directory entry waits for them. Said plainly before the
 * button does anything, because there is no undo.
 */
function DeleteAccount({ user }: { user: User }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const confirm = async () => {
    setDeleting(true);
    try {
      await user.delete();
      toast.success("Your account has been deleted");
      navigate("/");
    } catch (err) {
      toast.error(describe(err));
      setDeleting(false);
    }
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle className={sectionTitleClass}>Delete account</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-muted-foreground text-sm">
          Removes the account you sign in with. Your directory entry comes
          down, and a paid membership is not renewed after the current period.
          Registering again with this address starts a new application.
        </p>
        <Button variant="outline" onClick={() => setOpen(true)}>
          <Trash2 data-slot="icon" /> Delete account
        </Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete your account?</DialogTitle>
              <DialogDescription>
                You will be signed out and this sign-in will stop working.
                Your directory entry comes down, and a paid membership stops at
                the end of the period already paid for. Registering again with
                this address starts a new application.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)} disabled={deleting}>
                Keep my account
              </Button>
              <Button variant="destructive" onClick={confirm} disabled={deleting}>
                {deleting && <Spinner data-slot="icon" />}
                Delete account
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
